import type { TemplatePortfolioDraftSettings } from "@adpilot/shared";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useTemplateCategoryCandidates } from "../api";
import { AffixInput, parseAmount } from "./fields";

type CategoryTarget = TemplatePortfolioDraftSettings["categoryTarget"];

interface CategoryTargetCardProps {
  shopId: string;
  draftId: string;
  value: CategoryTarget;
  onSave: (next: CategoryTarget) => Promise<boolean>;
}

function RecommendedBadge() {
  const { t } = useTranslation("templatePortfolio");
  return (
    <span className="shrink-0 rounded-md bg-badge-information-background px-1.5 py-0.5 text-[11px] font-medium leading-[14px] text-badge-information-foreground">
      {t("preview.category.recommended")}
    </span>
  );
}

/** 商品投放 · 类目定向: category (SP recommendations or manual ID) + price band. */
export function CategoryTargetCard({ shopId, draftId, value, onSave }: CategoryTargetCardProps) {
  const { t } = useTranslation("templatePortfolio");
  const candidatesQuery = useTemplateCategoryCandidates(shopId, draftId);
  const candidates = candidatesQuery.data?.candidates ?? [];
  const recommendable = Boolean(candidatesQuery.data?.available) && candidates.length > 0;
  const known = candidates.some((candidate) => candidate.categoryId === value.categoryId);
  const [manualChosen, setManualChosen] = useState(false);
  const manual =
    candidatesQuery.isSuccess &&
    (!recommendable || manualChosen || (value.categoryId !== null && !known));

  // The template always targets a category: preselect recommendation #1 once.
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || value.categoryId !== null || !recommendable) return;
    const first = candidates[0];
    if (!first) return;
    preselected.current = true;
    void onSave({ ...value, categoryId: first.categoryId, categoryName: first.name });
  }, [candidates, onSave, recommendable, value]);

  const [manualId, setManualId] = useState(value.categoryId ?? "");
  const [minText, setMinText] = useState(String(value.priceGreaterThan));
  const [maxText, setMaxText] = useState(String(value.priceLessThan));
  useEffect(() => setManualId(value.categoryId ?? ""), [value.categoryId]);
  useEffect(() => setMinText(String(value.priceGreaterThan)), [value.priceGreaterThan]);
  useEffect(() => setMaxText(String(value.priceLessThan)), [value.priceLessThan]);

  const min = parseAmount(minText);
  const max = parseAmount(maxText);
  const priceInvalid = min === null || max === null || min < 0 || max <= 0 || min >= max;

  function savePrice() {
    if (priceInvalid || (min === value.priceGreaterThan && max === value.priceLessThan)) return;
    void onSave({ ...value, priceGreaterThan: min, priceLessThan: max });
  }

  function saveManualId() {
    const categoryId = manualId.trim();
    if (categoryId === "" || categoryId === value.categoryId) return;
    void onSave({ ...value, categoryId, categoryName: null });
  }

  const selectedIndex = candidates.findIndex((c) => c.categoryId === value.categoryId);
  const selected = candidates[selectedIndex];

  return (
    <section className="flex items-end gap-6 rounded-[14px] border border-line bg-surface px-5 py-4">
      <div className="flex w-[180px] shrink-0 flex-col gap-1 self-center">
        <h2 className="text-[13px] font-semibold leading-[18px] text-ink">
          {t("preview.category.title")}
        </h2>
        <p className="text-xs text-muted-foreground">{t("preview.category.description")}</p>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex justify-between">
          <span className="text-xs font-medium text-ink">{t("preview.category.label")}</span>
          {recommendable ? (
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => {
                setManualChosen(!manual);
                // Leaving manual mode: a hand-typed id is not in the list, so pick #1 again.
                const first = candidates[0];
                if (manual && !known && first) {
                  void onSave({ ...value, categoryId: first.categoryId, categoryName: first.name });
                }
              }}
            >
              {manual ? t("preview.category.useRecommended") : t("preview.category.manual")}
            </button>
          ) : null}
        </div>
        {candidatesQuery.isPending ? (
          <Skeleton className="h-9 w-full" />
        ) : manual ? (
          <Input
            value={manualId}
            placeholder={t("preview.category.manualPlaceholder")}
            aria-label={t("preview.category.label")}
            className="font-mono"
            onChange={(event) => setManualId(event.target.value)}
            onBlur={saveManualId}
            onKeyDown={(event) => event.key === "Enter" && saveManualId()}
          />
        ) : (
          <Select
            items={candidates.map((candidate) => ({
              value: candidate.categoryId,
              label: candidate.name,
            }))}
            value={value.categoryId}
            onValueChange={(categoryId) => {
              const candidate = candidates.find((c) => c.categoryId === categoryId);
              if (candidate && categoryId !== value.categoryId) {
                void onSave({ ...value, categoryId, categoryName: candidate.name });
              }
            }}
          >
            <SelectTrigger className="h-9 w-full" aria-label={t("preview.category.label")}>
              <span className="flex min-w-0 flex-1 items-center gap-2">
                {selected ? (
                  <>
                    <span className="truncate text-[13px] text-ink">{selected.name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {categoryContext(selected)}
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground">{t("preview.category.placeholder")}</span>
                )}
              </span>
              {selectedIndex === 0 ? <RecommendedBadge /> : null}
            </SelectTrigger>
            <SelectContent>
              {candidates.map((candidate, index) => (
                <SelectItem key={candidate.categoryId} value={candidate.categoryId}>
                  <span className="truncate">{candidate.name}</span>
                  <span className="truncate text-muted-foreground">
                    {categoryContext(candidate)}
                  </span>
                  {index === 0 ? <RecommendedBadge /> : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex w-[220px] shrink-0 flex-col gap-1.5">
        <span className="text-xs font-medium text-ink">{t("preview.category.priceBand")}</span>
        <div className="flex items-center gap-2">
          <AffixInput
            prefix="$"
            value={minText}
            aria-label={t("preview.category.priceMin")}
            aria-invalid={priceInvalid || undefined}
            onChange={(event) => setMinText(event.target.value)}
            onBlur={savePrice}
            onKeyDown={(event) => event.key === "Enter" && savePrice()}
          />
          <span className="text-xs text-muted-foreground">{t("preview.category.to")}</span>
          <AffixInput
            prefix="$"
            value={maxText}
            aria-label={t("preview.category.priceMax")}
            aria-invalid={priceInvalid || undefined}
            onChange={(event) => setMaxText(event.target.value)}
            onBlur={savePrice}
            onKeyDown={(event) => event.key === "Enter" && savePrice()}
          />
        </div>
        {priceInvalid ? (
          <p className="text-xs text-destructive">{t("preview.category.priceInvalid")}</p>
        ) : null}
      </div>
    </section>
  );
}

/** Paper TP-09: parent path then id, e.g. "Sports & Outdoors / Cardio Training · 3407781". */
function categoryContext(candidate: { categoryId: string; path: string | null }): string {
  const parents = candidate.path?.split(" / ").slice(0, -1).join(" / ");
  return parents ? `${parents} · ${candidate.categoryId}` : candidate.categoryId;
}
